import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { App, Produktlinie } from '@ps20-frontend/frontend-models';
import cloneDeep from 'lodash/cloneDeep';
import { Produktlinienname } from '@ps20/bff-models';
import { GlobalActions, GlobalPut } from '@ps20-frontend/shop-integration-redux';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { VhvActions } from '../vhvActions';
import { IAppState } from '../appState';
import { VersicherungsSummeGrenzen } from '../model/versicherungssummen';
import { selectVorschaedenUeberschrittenProduktlinie } from '../../app/shared/selectors/vorschaeden.selector';
import {
  selectIsErsatzgeschaeft,
  selectIsSelbstbeteiligungGt1000,
  selectIsErsatzgeschaeftWithPriorPremium,
} from '../../app/shared/selectors/vertragsoption.selector';
import { VersicherungssummeForLinie } from '../action-types/versicherungssummenResponseReceived';

export function* watchproduktlinienSaga(): SagaIterator {
  yield takeLatest(
    VhvActions.VERSICHERUNGSSUMMEN_RESPONSE_RECEIVED,
    safeSaga(updateProduktlinienStatus, App.VHV_V2),
  );
  yield takeLatest(
    VhvActions.VERSICHERUNGSSUMME_CHANGED,
    safeSaga(updateProduktlinienStatus, App.VHV_V2),
  );
  yield takeLatest(VhvActions.VORSCHAEDEN_CHANGED, safeSaga(updateProduktlinienStatus, App.VHV_V2));
  yield takeLatest(
    VhvActions.VORSCHAEDEN_FELS_CHANGED,
    safeSaga(updateProduktlinienStatus, App.VHV_V2),
  );
  yield takeLatest(
    VhvActions.VORSCHAEDEN_FAHRRAD_CHANGED,
    safeSaga(updateProduktlinienStatus, App.VHV_V2),
  );
  yield takeLatest(
    VhvActions.VERSICHERUNGS_OBJEKT_CHANGED,
    safeSaga(updateProduktlinienStatus, App.VHV_V2),
  );
  yield takeLatest(
    VhvActions.SELBSTBETEILIGUNG_CHANGED,
    safeSaga(updateProduktlinienStatus, App.VHV_V2),
  );
  yield takeLatest(
    VhvActions.WOHNFLAECHE_CHANGED,
    safeSaga(updateProduktlinienStatus, App.VHV_V2),
  );
}

export function* updateProduktlinienStatus(): SagaIterator {
  const vorschaedenUeberschritten: boolean = yield select(
    selectVorschaedenUeberschrittenProduktlinie,
  );
  const appState: IAppState = cloneDeep(yield select());
  const versSummen: Array<VersicherungssummeForLinie> = appState.versicherungssummen;
  const vorschaeden: boolean =
    appState.vorschaeden && appState.vorschaeden.vorhanden && vorschaedenUeberschritten;
  const unterversicherungsverzicht: boolean = appState.unterversicherungsverzicht;
  const produktlinien = appState.produkt.produktlinien;

  toggleProduktlinienActiveState(
    versSummen,
    produktlinien,
    vorschaeden,
    unterversicherungsverzicht,
    selectIsSelbstbeteiligungGt1000(appState),
  );
  handleNichtStaendigBewohnt(produktlinien, appState);
  handleWohnflaecheUnter80(produktlinien, appState);
  resetToDefaultProduktlinieIfNecessary(produktlinien, appState);
  const changeProduktlinienAction = new VhvActions().produktlinienChanged(produktlinien);
  yield put(changeProduktlinienAction);

  if (noProduktlinieSelected(produktlinien)) {
    const globalPut = GlobalPut.getInstance();
    yield call(
      globalPut.put.bind(globalPut),
      new GlobalActions().gesamtpreisChanged(0, false, App.VHV_V2, appState.angebotsId),
    );
  }
}

function isProduktlinieSOrM(versSumme: VersicherungssummeForLinie): boolean {
  return (
    versSumme.produktlinie === Produktlinienname.BASIS ||
    versSumme.produktlinie === Produktlinienname.SMART
  );
}

function shouldDeactivateLinieSOrM(
  versSumme: number,
  vorschaedenVorhanden: boolean,
  unterversicherungsverzicht: boolean,
  isSelbstbeteiligungGt1000: boolean
) {
  return (
    versSumme > VersicherungsSummeGrenzen.MIN_VERSSUMME_FUER_S_AND_M ||
    vorschaedenVorhanden ||
    unterversicherungsverzicht ||
    isSelbstbeteiligungGt1000
  );
}

function toggleProduktlinienActiveState(
  versSummen: Array<VersicherungssummeForLinie>,
  produktlinien: Produktlinie[],
  vorschaedenVorhanden: boolean,
  unterversicherungsverzicht: boolean,
  isSelbstbeteiligungGt1000: boolean
) {
  produktlinien.forEach(produktlinie => (produktlinie.deactivated = false));

  versSummen.forEach(versSumme => {
    if (
      isProduktlinieSOrM(versSumme) &&
      shouldDeactivateLinieSOrM(
        versSumme.summe,
        vorschaedenVorhanden,
        unterversicherungsverzicht,
        isSelbstbeteiligungGt1000,
      )
    ) {
      deactivateProduktlinie(produktlinien, versSumme.produktlinie);
    }
  });
}

// Safety net for the VHV2X-Ersatzgeschaeft-PREMIUM default: the initial override happens
// once in produkt.reducer.ts (VORBELEGUNG_FINISHED); this re-applies the default if a later
// deactivation (e.g. handleNichtStaendigBewohnt) clears the selection again mid-flow.
export function resetToDefaultProduktlinieIfNecessary(
  produktlinien: Produktlinie[],
  appState: IAppState,
) {
  const isSomeProduktlinieSelected = produktlinien.some(
    produktlinie => produktlinie.selected === true,
  );
  if (isSomeProduktlinieSelected) {
    return;
  }

  const candidateProduktlinienamen = selectIsErsatzgeschaeftWithPriorPremium(appState)
    ? [Produktlinienname.PREMIUM, Produktlinienname.KOMFORT]
    : [Produktlinienname.KOMFORT];

  for (const produktlinienname of candidateProduktlinienamen) {
    const produktlinie = produktlinien.find(
      candidate => candidate.produktName === produktlinienname && !candidate.deactivated,
    );
    if (produktlinie) {
      produktlinie.selected = true;
      return;
    }
  }
}

function handleNichtStaendigBewohnt(produktlinien: Array<Produktlinie>, appState: IAppState) {
  const nichtStaendigBewohnt = !appState.versObjekt?.staendigBewohnt;
  const versSumme: number = appState.vertragsoptionen?.versicherungssumme;

  if (nichtStaendigBewohnt) {
    deactivateProduktlinie(produktlinien, Produktlinienname.PREMIUM);
  }
  const shouldDeactivateAllWithoutKomfort =
    (nichtStaendigBewohnt &&
      versSumme > VersicherungsSummeGrenzen.MAX_VERSSUMME_NICHT_STAENDIG_BEWOHNT) ||
    (!nichtStaendigBewohnt && versSumme > VersicherungsSummeGrenzen.MAX_VERSSUMME);
  if (shouldDeactivateAllWithoutKomfort) {
    const premiumWeiterhinMoeglichInErsatzgeschaeft =
      !nichtStaendigBewohnt && selectIsErsatzgeschaeft(appState);

    [Produktlinienname.BASIS, Produktlinienname.SMART].forEach(productLine =>
      deactivateProduktlinie(produktlinien, productLine),
    );
    if (!premiumWeiterhinMoeglichInErsatzgeschaeft) {
      deactivateProduktlinie(produktlinien, Produktlinienname.PREMIUM);
      const productLineL = produktlinien.find(
        productLine => productLine.produktName === Produktlinienname.KOMFORT,
      );
      if (productLineL) {
        productLineL.selected = true;
        productLineL.deactivated = false;
      }
    }
  }
}

function handleWohnflaecheUnter80(produktlinien: Array<Produktlinie>, appState: IAppState) {
  if (appState.versObjekt?.wohnflaeche < 80) {
    deactivateProduktlinie(produktlinien, Produktlinienname.PREMIUM);
  }
}

export function deactivateProduktlinie(
  produktlinien: Produktlinie[],
  produktlineType: Produktlinienname,
) {
  produktlinien.forEach(tarifItem => {
    if (produktlineType === tarifItem.produktName) {
      tarifItem.deactivated = true;
      tarifItem.selected = false;
    }
  });
}

export function activateProduktlinie(
  produktlinien: Produktlinie[],
  produktlineType: Produktlinienname,
) {
  produktlinien.forEach(tarifItem => {
    if (produktlineType === tarifItem.produktName) {
      tarifItem.deactivated = false;
    }
  });
}

function noProduktlinieSelected(produktlinien: Array<Produktlinie>): boolean {
  return !produktlinien.some(produktlinie => produktlinie.selected);
}
